import React, { useState } from 'react';

import type { ApiClient } from '../api/client';
import { reportErrorLocal } from '../utils/errors';
import ErrorBanner from './ErrorBanner';
import type { AppItem, Conversation, ConversationMode } from '../types';

const STORAGE_KEY = 'last_conversation_preferences';

interface StoredPreferences {
  app_id?: string; // Shared between single and multi-app modes
  scope?: ConversationMode;
  interaction_mode?: 'chat' | 'interactive_agent';
  visibility?: 'private' | 'project';
  message?: string;
}

function getStoredPreferences(): StoredPreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.error('Failed to read conversation preferences from localStorage', err);
  }
  return null;
}

function setStoredPreferences(prefs: StoredPreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (err) {
    console.error('Failed to save conversation preferences to localStorage', err);
  }
}

function setStoredMessage(message: string) {
  try {
    const stored = getStoredPreferences() ?? {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, message }));
  } catch (err) {
    console.error('Failed to save message draft to localStorage', err);
  }
}

export type ConversationCreateFormProps = {
  apiClient: ApiClient;
  apps: AppItem[];
  baseAppId?: string;
  allowNoApp?: boolean;
  allowMulti?: boolean;
  allowAppSelect?: boolean;
  onCreated?: (conversation: Conversation) => void;
  setError: (message: string | null) => void;
};

export function ConversationCreateForm({
  apiClient,
  apps,
  baseAppId,
  allowNoApp = true,
  allowMulti = true,
  allowAppSelect = true,
  onCreated,
  setError
}: ConversationCreateFormProps) {
  const [scope, setScope] = useState<ConversationMode>(() => {
    // When in app context, always default to 'app'
    if (baseAppId) return 'app';
    // Otherwise restore from storage or default to 'app'
    const stored = apiClient.mode === 'tool-browser' ? null : getStoredPreferences();
    return stored?.scope ?? 'app';
  });
  // Single shared app selection for both single and multi-app modes
  const [selectedAppId, setSelectedAppId] = useState(() => {
    // When in app context, always use the base app
    if (baseAppId) return baseAppId;
    // Otherwise restore from storage or default to empty
    const stored = apiClient.mode === 'tool-browser' ? null : getStoredPreferences();
    return stored?.app_id ?? '';
  });
  const [selectedSecondaryAppIds, setSelectedSecondaryAppIds] = useState<string[]>([]);
  const [message, setMessage] = useState<string>(() => {
    const stored = apiClient.mode === 'tool-browser' ? null : getStoredPreferences();
    return stored?.message ?? '';
  });
  const [interactionMode, setInteractionMode] = useState<'chat' | 'interactive_agent'>(() => {
    const stored = apiClient.mode === 'tool-browser' ? null : getStoredPreferences();
    return stored?.interaction_mode ?? 'chat';
  });
  const [visibility, setVisibility] = useState<'private' | 'project'>(() => {
    const stored = apiClient.mode === 'tool-browser' ? null : getStoredPreferences();
    return stored?.visibility ?? 'private';
  });
  const [formError, setFormError] = useState<string | null>(null);

  const defaultAppId = apps[0]?.id ?? '';
  const selectedAppIsAvailable = apps.some((app) => app.id === selectedAppId);
  // A locally persisted selection can outlive a membership change. Never send
  // that stale app id back to the server from the creation form.
  const effectiveSelectedAppId =
    baseAppId || (selectedAppIsAvailable ? selectedAppId : '') || defaultAppId;
  const effectiveScope = scope === 'none' && !allowNoApp ? 'app' : scope;

  function toggleSecondarySelection(appId: string) {
    setSelectedSecondaryAppIds((prev) => {
      if (prev.includes(appId)) {
        return prev.filter((id) => id !== appId);
      }
      return [...prev, appId];
    });
  }

  async function startConversation(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const form = new FormData(formEl);
    const title = String(form.get('title') ?? '').trim();
    if (!title) {
      setFormError('Conversation title required');
      return;
    }
    const payload: {
      title: string;
      app_id?: string;
      app_ids?: string[];
      primary_app_id?: string;
      interaction_mode: 'chat' | 'interactive_agent';
      visibility: 'private' | 'project';
    } = {
      title,
      interaction_mode: interactionMode,
      visibility: effectiveScope === 'none' ? 'private' : visibility
    };

    if (effectiveScope === 'none') {
      if (!allowNoApp) {
        setFormError('No-app conversations are not available here.');
        return;
      }
      payload.app_ids = [];
    } else if (effectiveScope === 'multi') {
      if (!allowMulti) {
        setFormError('Multi-app conversations are not available here.');
        return;
      }
      const primaryAppId = effectiveSelectedAppId;
      if (!primaryAppId) {
        setFormError('Select a primary app for this conversation.');
        return;
      }
      const secondaryIds = selectedSecondaryAppIds.filter((id) => id !== primaryAppId);
      if (secondaryIds.length === 0) {
        setFormError('Select at least one secondary app for a multi-app conversation.');
        return;
      }
      payload.primary_app_id = primaryAppId;
      payload.app_ids = Array.from(new Set([primaryAppId, ...secondaryIds]));
    } else {
      const appId = effectiveSelectedAppId;
      if (!appId) {
        setFormError('Select an app for this conversation.');
        return;
      }
      payload.app_id = appId;
    }

    try {
      const created = await apiClient.post<Conversation>('/api/conversations', payload);
      // Save preferences for next time (only if not in app context)
      if (!baseAppId && apiClient.mode !== 'tool-browser') {
        setStoredPreferences({
          app_id: effectiveSelectedAppId,
          scope: effectiveScope,
          interaction_mode: interactionMode,
          visibility
        });
      }
      // Clear the stored message after successful send
      if (apiClient.mode !== 'tool-browser') setStoredMessage('');
      formEl.reset();
      setSelectedSecondaryAppIds([]);
      setScope('app');
      setMessage('');
      setFormError(null);
      if (onCreated) {
        onCreated(created);
      }
    } catch (err) {
      reportErrorLocal('Failed to start conversation', err, setError, setFormError);
    }
  }

  const multiPrimaryAppId = effectiveSelectedAppId;
  const secondarySelectableApps = apps.filter((app) => app.id !== multiPrimaryAppId);

  return (
    <form onSubmit={startConversation} className="stack">
      {formError && <ErrorBanner message={formError} onDismiss={() => setFormError(null)} />}
      <div className="inline-form">
        <input
          name="title"
          placeholder="Conversation title"
          required
          value={message}
          onChange={(e) => {
            const value = e.target.value;
            setMessage(value);
            if (apiClient.mode !== 'tool-browser') setStoredMessage(value);
          }}
        />
        <button type="submit" className="primary">Start conversation</button>
      </div>
      <div className="row">
        <span>Experience</span>
        <select
          aria-label="Conversation experience"
          value={interactionMode}
          onChange={(event) =>
            setInteractionMode(event.target.value as 'chat' | 'interactive_agent')
          }
        >
          <option value="chat">Chat</option>
          <option value="interactive_agent">Interactive agent</option>
        </select>
      </div>
      {effectiveScope !== 'none' && (
        <div className="row">
          <span>Visibility</span>
          <select
            aria-label="Conversation visibility"
            value={visibility}
            onChange={(event) => setVisibility(event.target.value as 'private' | 'project')}
          >
            <option value="private">Private to me</option>
            <option value="project">Project members</option>
          </select>
        </div>
      )}
      <div className="row">
        <span>Scope</span>
        <select
          aria-label="Conversation scope"
          value={effectiveScope}
          onChange={(event) => {
            const next = event.target.value as ConversationMode;
            setScope(next);
            if (next !== 'multi') {
              setSelectedSecondaryAppIds([]);
            }
          }}
        >
          <option value="app">Single app</option>
          {allowMulti && <option value="multi">Multiple apps</option>}
          {allowNoApp && <option value="none">No app (use self runner)</option>}
        </select>
      </div>
      {effectiveScope === 'app' && allowAppSelect && !baseAppId && (
        <div className="row">
          <span>App</span>
          <select
            value={effectiveSelectedAppId}
            onChange={(event) => setSelectedAppId(event.target.value)}
          >
            {apps.length === 0 && <option value="">No apps</option>}
            {apps.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name || app.slug}
              </option>
            ))}
          </select>
        </div>
      )}
      {effectiveScope === 'multi' && (
        <div className="stack">
          <div className="hint">
            {baseAppId
              ? 'Primary app is the current app. Select one or more secondary apps.'
              : 'Select a primary app (used for the agent Dockerfile) and one or more secondary apps.'}
          </div>
          {!baseAppId && (
            <div className="row">
              <span>Primary app</span>
              <select
                value={effectiveSelectedAppId}
                onChange={(event) => {
                  const nextPrimary = event.target.value;
                  setSelectedAppId(nextPrimary);
                  setSelectedSecondaryAppIds((prev) => prev.filter((id) => id !== nextPrimary));
                }}
              >
                {apps.length === 0 && <option value="">No apps</option>}
                {apps.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name || app.slug}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="stack">
            {secondarySelectableApps.length === 0 && <div className="hint">No secondary apps available.</div>}
            {secondarySelectableApps.map((app) => (
              <label key={app.id} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={selectedSecondaryAppIds.includes(app.id)}
                  onChange={() => toggleSecondarySelection(app.id)}
                />
                <span>{app.name || app.slug}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {effectiveScope === 'none' && allowNoApp && (
        <div className="hint">Uses the self app runner and starts with an empty workspace.</div>
      )}
    </form>
  );
}
